/**
 * SignalsFeed — sticky stream of latest market signals.
 *
 * Each row is colour-coded by the user's perspective: rows matched
 * BY their strategies get a `matched` pill so the eye lands on those.
 * Phase 8 swaps the polling for a WebSocket stream — same shape.
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { timeAgo } from '@/lib/format'
import type { RecentSignal } from '@/hooks/useDashboardApi'

interface SignalsFeedProps {
  items: RecentSignal[] | undefined
  loading?: boolean
}

export function SignalsFeed({ items, loading }: SignalsFeedProps) {
  const { t } = useTranslation('app')

  return (
    <section className="bg-ink h-full flex flex-col">
      <header className="flex items-baseline px-5 py-3 border-b border-line">
        <span
          className="dot mr-2 animate-pulse"
          style={{ background: 'var(--pulse)' }}
        />
        <h3 className="text-sm font-semibold">
          {t('dashboard.signals_feed.title')}
        </h3>
        <span className="ml-auto text-xs text-textFaint font-mono">
          {t('dashboard.signals_feed.subtitle')}
        </span>
      </header>

      {loading || items === undefined ? (
        <div className="flex-1 grid place-items-center text-textFaint text-sm py-8">—</div>
      ) : items.length === 0 ? (
        <div className="flex-1 grid place-items-center text-textFaint text-sm py-8">
          {t('dashboard.signals_feed.empty')}
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto divide-y divide-line/40">
          {items.map((s) => <SignalRow key={s.id} sig={s} />)}
        </ul>
      )}
    </section>
  )
}

function SignalRow({ sig }: { sig: RecentSignal }) {
  const { t } = useTranslation('app')
  const dirTone = sig.direction === 'long' ? 'text-pulse' : 'text-rose'
  return (
    <li className={cn(
      'flex items-center gap-2 px-5 py-2 text-xs font-mono',
      sig.matched_for_me && 'bg-fire-faint/40',
    )}>
      <span className="text-textFaint w-14 shrink-0">
        {timeAgo(sig.ts)}
      </span>
      <span className={cn('uppercase font-semibold w-10 shrink-0', dirTone)}>
        {sig.direction === 'long' ? 'L' : 'S'}
      </span>
      <span className="font-semibold">{sig.symbol}</span>
      <span className="text-textFaint">{sig.timeframe}</span>
      {sig.score != null && (
        <span className="text-textDim ml-1">{sig.score.toFixed(1)}</span>
      )}
      {sig.matched_for_me && (
        <span className="pill bg-fire-faint text-fire ml-auto">
          {t('dashboard.signals_feed.matched')}
        </span>
      )}
    </li>
  )
}
